import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const DEFAULT_JSON = `{
  "name": "quto",
  "version": 1,
  "tools": [
    { "id": "json-yaml", "active": true },
    { "id": "csv-json", "active": true }
  ],
  "meta": {
    "owner": "Adhish",
    "tags": ["dev", "utility"]
  }
}`;

const DEFAULT_YAML = `name: quto
version: 1
tools:
  - id: json-yaml
    active: true
  - id: csv-json
    active: true
meta:
  owner: Adhish
  tags:
    - dev
    - utility
`;

// ---------- JSON → YAML ----------

const PLAIN_KEY_RE = /^[A-Za-z_][A-Za-z0-9_\-]*$/;
const RESERVED_WORDS = new Set([
  "y", "Y", "yes", "Yes", "YES", "n", "N", "no", "No", "NO",
  "true", "True", "TRUE", "false", "False", "FALSE",
  "on", "On", "ON", "off", "Off", "OFF",
  "null", "Null", "NULL", "~", "",
]);

const needsDoubleQuoting = (s) => {
  if (s.length === 0) return true;
  if (/[\u0000-\u001f\u007f]/.test(s)) return true;
  if (s.includes("\n") || s.includes("\t")) return true;
  return false;
};

const escapeDoubleQuoted = (s) =>
  s.replace(/[\\"\u0000-\u001f\u007f]/g, (ch) => {
    if (ch === "\\") return "\\\\";
    if (ch === '"') return '\\"';
    if (ch === "\n") return "\\n";
    if (ch === "\r") return "\\r";
    if (ch === "\t") return "\\t";
    if (ch === "\b") return "\\b";
    if (ch === "\f") return "\\f";
    const code = ch.charCodeAt(0);
    return "\\x" + code.toString(16).padStart(2, "0");
  });

const formatScalar = (value) => {
  if (value === null || value === undefined) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      if (Number.isNaN(value)) return ".nan";
      return value > 0 ? ".inf" : "-.inf";
    }
    return String(value);
  }
  const s = String(value);
  if (needsDoubleQuoting(s)) {
    return `"${escapeDoubleQuoted(s)}"`;
  }
  // Quote anything that could be reinterpreted as another type
  if (RESERVED_WORDS.has(s)) return `"${s}"`;
  if (/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(s)) return `"${s}"`;
  if (/^[-:?#&*!|>'"%@`,\[\]{}]/.test(s)) return `"${escapeDoubleQuoted(s)}"`;
  if (s.includes(": ") || s.endsWith(":") || s.includes(" #")) {
    return `"${escapeDoubleQuoted(s)}"`;
  }
  if (s !== s.trim()) return `"${escapeDoubleQuoted(s)}"`;
  return s;
};

const formatKey = (key) => {
  const s = String(key);
  if (PLAIN_KEY_RE.test(s) && !RESERVED_WORDS.has(s)) return s;
  return `"${escapeDoubleQuoted(s)}"`;
};

const isContainer = (v) => v !== null && typeof v === "object";
const isEmptyContainer = (v) =>
  Array.isArray(v) ? v.length === 0 : Object.keys(v).length === 0;

const jsonValueToYaml = (value, indentSize) => {
  if (!isContainer(value) || isEmptyContainer(value)) {
    return formatScalar(value) + "\n";
  }
  const lines = [];
  writeNode(value, 0, lines, indentSize);
  return lines.join("\n") + "\n";
};

const scalarOrFlowEmpty = (v) => {
  if (isContainer(v) && isEmptyContainer(v)) {
    return Array.isArray(v) ? "[]" : "{}";
  }
  return formatScalar(v);
};

const writeNode = (value, indent, lines, indentSize) => {
  const pad = " ".repeat(indent);
  if (Array.isArray(value)) {
    for (const item of value) {
      if (isContainer(item) && !isEmptyContainer(item)) {
        if (Array.isArray(item)) {
          lines.push(pad + "-");
          writeNode(item, indent + indentSize, lines, indentSize);
        } else {
          const keys = Object.keys(item);
          keys.forEach((k, idx) => {
            const v = item[k];
            const prefix = idx === 0 ? pad + "- " : pad + "  ";
            if (isContainer(v) && !isEmptyContainer(v)) {
              lines.push(prefix + formatKey(k) + ":");
              writeNode(v, indent + indentSize, lines, indentSize);
            } else {
              lines.push(prefix + formatKey(k) + ": " + scalarOrFlowEmpty(v));
            }
          });
        }
      } else {
        lines.push(pad + "- " + scalarOrFlowEmpty(item));
      }
    }
  } else {
    for (const k of Object.keys(value)) {
      const v = value[k];
      if (isContainer(v) && !isEmptyContainer(v)) {
        lines.push(pad + formatKey(k) + ":");
        writeNode(v, indent + indentSize, lines, indentSize);
      } else {
        lines.push(pad + formatKey(k) + ": " + scalarOrFlowEmpty(v));
      }
    }
  }
};

// ---------- YAML → JSON ----------

const stripInlineComment = (text) => {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) {
      if (i > 0 && text[i - 1] === "\\") continue;
      inDouble = !inDouble;
    } else if (ch === "#" && !inSingle && !inDouble) {
      if (i === 0 || /\s/.test(text[i - 1])) {
        return text.slice(0, i).replace(/\s+$/, "");
      }
    }
  }
  return text;
};

const parseScalarToken = (raw) => {
  const text = raw.trim();
  if (text === "" || text === "~" || text === "null" || text === "Null" || text === "NULL") return null;
  if (text === "true" || text === "True" || text === "TRUE") return true;
  if (text === "false" || text === "False" || text === "FALSE") return false;
  if (text === ".nan" || text === ".NaN" || text === ".NAN") return NaN;
  if (text === ".inf" || text === ".Inf" || text === ".INF") return Infinity;
  if (text === "-.inf" || text === "-.Inf" || text === "-.INF") return -Infinity;
  if (text[0] === '"' && text[text.length - 1] === '"' && text.length >= 2) {
    return decodeDoubleQuoted(text.slice(1, -1));
  }
  if (text[0] === "'" && text[text.length - 1] === "'" && text.length >= 2) {
    return text.slice(1, -1).replace(/''/g, "'");
  }
  if (/^-?(?:0|[1-9]\d*)$/.test(text)) {
    const n = Number(text);
    if (Number.isSafeInteger(n)) return n;
  }
  if (/^-?(?:0|[1-9]\d*)\.\d+(?:[eE][+-]?\d+)?$/.test(text)) {
    const n = Number(text);
    if (Number.isFinite(n)) return n;
  }
  if (/^-?\d+(?:\.\d+)?[eE][+-]?\d+$/.test(text)) {
    const n = Number(text);
    if (Number.isFinite(n)) return n;
  }
  if (text === "[]") return [];
  if (text === "{}") return {};
  if (text[0] === "[" || text[0] === "{") {
    return parseFlow(text);
  }
  return text;
};

const decodeDoubleQuoted = (s) => {
  let out = "";
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch !== "\\") {
      out += ch;
      continue;
    }
    const next = s[i + 1];
    i += 1;
    if (next === "n") out += "\n";
    else if (next === "r") out += "\r";
    else if (next === "t") out += "\t";
    else if (next === "b") out += "\b";
    else if (next === "f") out += "\f";
    else if (next === "0") out += "\0";
    else if (next === '"') out += '"';
    else if (next === "\\") out += "\\";
    else if (next === "/") out += "/";
    else if (next === "x") {
      const hex = s.slice(i + 1, i + 3);
      if (/^[0-9a-fA-F]{2}$/.test(hex)) {
        out += String.fromCharCode(parseInt(hex, 16));
        i += 2;
      } else {
        out += next;
      }
    } else if (next === "u") {
      const hex = s.slice(i + 1, i + 5);
      if (/^[0-9a-fA-F]{4}$/.test(hex)) {
        out += String.fromCharCode(parseInt(hex, 16));
        i += 4;
      } else {
        out += next;
      }
    } else {
      out += next ?? "";
    }
  }
  return out;
};

// Minimal flow-style parser for [a, b], {a: 1, b: 2}, nesting, and quoted scalars.
const parseFlow = (text) => {
  const tokens = tokenizeFlow(text);
  const state = { pos: 0, tokens };
  const value = readFlowValue(state);
  return value;
};

const tokenizeFlow = (text) => {
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) { i += 1; continue; }
    if (ch === "[" || ch === "]" || ch === "{" || ch === "}" || ch === "," || ch === ":") {
      tokens.push({ kind: ch });
      i += 1;
      continue;
    }
    if (ch === '"' || ch === "'") {
      const quote = ch;
      let j = i + 1;
      let str = "";
      while (j < text.length) {
        if (quote === '"' && text[j] === "\\") {
          str += text.slice(j, j + 2);
          j += 2;
          continue;
        }
        if (text[j] === quote) {
          if (quote === "'" && text[j + 1] === "'") { str += "'"; j += 2; continue; }
          break;
        }
        str += text[j];
        j += 1;
      }
      const raw = quote + str + quote;
      tokens.push({ kind: "scalar", value: parseScalarToken(raw) });
      i = j + 1;
      continue;
    }
    let j = i;
    while (j < text.length && !/[\[\]{},:]/.test(text[j])) j += 1;
    const raw = text.slice(i, j).trim();
    if (raw !== "") tokens.push({ kind: "scalar", value: parseScalarToken(raw), raw });
    i = j;
  }
  return tokens;
};

const readFlowValue = (state) => {
  const tok = state.tokens[state.pos];
  if (!tok) return null;
  if (tok.kind === "[") {
    state.pos += 1;
    const arr = [];
    while (state.pos < state.tokens.length && state.tokens[state.pos].kind !== "]") {
      arr.push(readFlowValue(state));
      if (state.tokens[state.pos]?.kind === ",") state.pos += 1;
    }
    state.pos += 1;
    return arr;
  }
  if (tok.kind === "{") {
    state.pos += 1;
    const obj = {};
    while (state.pos < state.tokens.length && state.tokens[state.pos].kind !== "}") {
      const keyTok = state.tokens[state.pos];
      const key = keyTok.kind === "scalar"
        ? (typeof keyTok.value === "string" ? keyTok.value : (keyTok.raw ?? String(keyTok.value)))
        : "";
      state.pos += 1;
      if (state.tokens[state.pos]?.kind === ":") state.pos += 1;
      obj[key] = readFlowValue(state);
      if (state.tokens[state.pos]?.kind === ",") state.pos += 1;
    }
    state.pos += 1;
    return obj;
  }
  if (tok.kind === "scalar") {
    state.pos += 1;
    return tok.value;
  }
  state.pos += 1;
  return null;
};

const splitKeyValue = (text) => {
  let inSingle = false;
  let inDouble = false;
  let depth = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) {
      if (i > 0 && text[i - 1] === "\\") continue;
      inDouble = !inDouble;
    } else if (!inSingle && !inDouble) {
      if (ch === "[" || ch === "{") depth += 1;
      else if (ch === "]" || ch === "}") depth -= 1;
      else if (ch === ":" && depth === 0) {
        const after = text[i + 1];
        if (after === undefined || after === " " || after === "\t") {
          return {
            key: text.slice(0, i).trim(),
            value: text.slice(i + 1).trim(),
          };
        }
      }
    }
  }
  return null;
};

const unquoteKey = (key) => {
  if (key.length >= 2) {
    if (key[0] === '"' && key[key.length - 1] === '"') {
      return decodeDoubleQuoted(key.slice(1, -1));
    }
    if (key[0] === "'" && key[key.length - 1] === "'") {
      return key.slice(1, -1).replace(/''/g, "'");
    }
  }
  return key;
};

const isMappingLine = (text) => splitKeyValue(text) !== null;
const isSequenceLine = (text) => text === "-" || text.startsWith("- ");

const yamlToJson = (text) => {
  const cleaned = text.replace(/^\uFEFF/, "").replace(/\t/g, "    ");
  const rawLines = cleaned.split(/\r?\n/);
  const lines = [];
  for (let i = 0; i < rawLines.length; i += 1) {
    const raw = rawLines[i];
    if (/^\s*---\s*$/.test(raw) || /^\s*\.\.\.\s*$/.test(raw)) continue;
    const match = raw.match(/^(\s*)(.*)$/);
    const indent = match[1].length;
    let body = match[2];
    body = stripInlineComment(body).replace(/\s+$/, "");
    if (body === "") continue;
    if (body.startsWith("#")) continue;
    lines.push({ indent, text: body });
  }
  if (lines.length === 0) return null;

  const state = { pos: 0 };

  const parseNode = (baseIndent) => {
    if (state.pos >= lines.length) return null;
    const first = lines[state.pos];
    if (first.indent < baseIndent) return null;
    const ind = first.indent;
    if (isSequenceLine(first.text)) return parseSequence(ind);
    if (isMappingLine(first.text)) return parseMapping(ind);
    state.pos += 1;
    return parseScalarToken(first.text);
  };

  const parseSequence = (indent) => {
    const arr = [];
    while (state.pos < lines.length) {
      const line = lines[state.pos];
      if (line.indent !== indent) break;
      if (!isSequenceLine(line.text)) break;
      state.pos += 1;
      const after = line.text === "-" ? "" : line.text.slice(2).trim();
      if (after === "") {
        arr.push(parseNode(indent + 1));
        continue;
      }
      const kv = splitKeyValue(after);
      if (kv !== null) {
        const map = {};
        addMappingEntry(map, kv, indent + 2);
        while (state.pos < lines.length) {
          const next = lines[state.pos];
          if (next.indent !== indent + 2) break;
          if (!isMappingLine(next.text)) break;
          state.pos += 1;
          const nkv = splitKeyValue(next.text);
          addMappingEntry(map, nkv, indent + 2);
        }
        arr.push(map);
        continue;
      }
      if (isSequenceLine(after)) {
        const sub = [];
        const inner = after === "-" ? "" : after.slice(2).trim();
        if (inner !== "") {
          sub.push(parseScalarToken(inner));
        } else {
          sub.push(parseNode(indent + 3));
        }
        while (state.pos < lines.length) {
          const next = lines[state.pos];
          if (next.indent !== indent + 2) break;
          if (!isSequenceLine(next.text)) break;
          state.pos += 1;
          const a = next.text === "-" ? "" : next.text.slice(2).trim();
          if (a === "") sub.push(parseNode(indent + 3));
          else sub.push(parseScalarToken(a));
        }
        arr.push(sub);
        continue;
      }
      arr.push(parseScalarToken(after));
    }
    return arr;
  };

  const parseMapping = (indent) => {
    const map = {};
    while (state.pos < lines.length) {
      const line = lines[state.pos];
      if (line.indent !== indent) break;
      const kv = splitKeyValue(line.text);
      if (kv === null) break;
      state.pos += 1;
      addMappingEntry(map, kv, indent);
    }
    return map;
  };

  const addMappingEntry = (map, kv, ownIndent) => {
    const key = unquoteKey(kv.key);
    if (kv.value === "") {
      map[key] = parseNode(ownIndent + 1);
    } else {
      map[key] = parseScalarToken(kv.value);
    }
  };

  return parseNode(0);
};

// ---------- Component ----------

const JsonYamlConverter = () => {
  const { t } = useTranslation();
  const [direction, setDirection] = useState("json-to-yaml");
  const [jsonText, setJsonText] = useState(DEFAULT_JSON);
  const [yamlText, setYamlText] = useState(DEFAULT_YAML);
  const [indentSize, setIndentSize] = useState(2);
  const [pretty, setPretty] = useState(true);

  const isJsonToYaml = direction === "json-to-yaml";
  const inputValue = isJsonToYaml ? jsonText : yamlText;
  const setInput = isJsonToYaml ? setJsonText : setYamlText;

  const result = useMemo(() => {
    if (isJsonToYaml) {
      if (!jsonText.trim()) return { ok: true, output: "" };
      let data;
      try {
        data = JSON.parse(jsonText);
      } catch (err) {
        return { ok: false, errorKey: "json_yaml_error_invalid_json", errorDetail: err.message };
      }
      try {
        return { ok: true, output: jsonValueToYaml(data, indentSize) };
      } catch (err) {
        return { ok: false, errorKey: "json_yaml_error_invalid_yaml", errorDetail: err.message };
      }
    }
    if (!yamlText.trim()) return { ok: true, output: "" };
    let parsed;
    try {
      parsed = yamlToJson(yamlText);
    } catch (err) {
      return { ok: false, errorKey: "json_yaml_error_invalid_yaml", errorDetail: err.message };
    }
    const replacer = (_key, value) => {
      if (typeof value === "number" && !Number.isFinite(value)) return null;
      return value;
    };
    try {
      const out = JSON.stringify(parsed, replacer, pretty ? indentSize : 0);
      return { ok: true, output: out ?? "" };
    } catch (err) {
      return { ok: false, errorKey: "json_yaml_error_invalid_yaml", errorDetail: err.message };
    }
  }, [isJsonToYaml, jsonText, yamlText, indentSize, pretty]);

  const swap = () => {
    if (result.ok && result.output) {
      if (isJsonToYaml) setYamlText(result.output);
      else setJsonText(result.output);
    }
    setDirection(isJsonToYaml ? "yaml-to-json" : "json-to-yaml");
  };

  const copy = async () => {
    if (!result.ok || !result.output) {
      toast.error(t("json_yaml_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(result.output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const download = () => {
    if (!result.ok || !result.output) {
      toast.error(t("json_yaml_nothing_to_copy"));
      return;
    }
    const filename = isJsonToYaml ? "converted.yaml" : "converted.json";
    const mime = isJsonToYaml ? "application/x-yaml" : "application/json";
    const blob = new Blob([result.output], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clear = () => setInput("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("json_yaml_title")}
            </h1>
            <p className="text-gray-600">{t("json_yaml_intro")}</p>

            <div className="flex flex-wrap items-center gap-2">
              <StandardButton
                onClick={() => setDirection("json-to-yaml")}
                type={isJsonToYaml ? "primary" : "secondary"}
                variant={isJsonToYaml ? undefined : "outlined"}
                size="small"
              >
                {t("json_yaml_dir_json_to_yaml")}
              </StandardButton>
              <StandardButton
                onClick={() => setDirection("yaml-to-json")}
                type={!isJsonToYaml ? "primary" : "secondary"}
                variant={!isJsonToYaml ? undefined : "outlined"}
                size="small"
              >
                {t("json_yaml_dir_yaml_to_json")}
              </StandardButton>
              <StandardButton
                onClick={swap}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("json_yaml_swap")}
              </StandardButton>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("json_yaml_indent_label")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {[2, 4].map((n) => (
                    <StandardButton
                      key={n}
                      onClick={() => setIndentSize(n)}
                      type={indentSize === n ? "primary" : "secondary"}
                      variant={indentSize === n ? undefined : "outlined"}
                      size="small"
                    >
                      <span className="font-mono mr-1">{n}</span>
                      {t("json_yaml_indent_spaces")}
                    </StandardButton>
                  ))}
                </div>
              </div>

              {!isJsonToYaml && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={pretty}
                    onChange={(e) => setPretty(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("json_yaml_pretty_print")}
                </label>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {isJsonToYaml
                    ? t("json_yaml_input_label_json")
                    : t("json_yaml_input_label_yaml")}
                </label>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={!inputValue}
                >
                  {t("json_yaml_clear")}
                </StandardButton>
              </div>
              <textarea
                value={inputValue}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  isJsonToYaml
                    ? t("json_yaml_placeholder_json")
                    : t("json_yaml_placeholder_yaml")
                }
                rows={10}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {isJsonToYaml
                    ? t("json_yaml_output_label_yaml")
                    : t("json_yaml_output_label_json")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={download}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!result.ok || !result.output}
                  >
                    {t("json_yaml_download")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!result.ok || !result.output}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              {result.ok ? (
                <pre className="w-full min-h-[6rem] max-h-[24rem] overflow-auto p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre text-gray-800">
                  {result.output || (
                    <em className="text-gray-400 not-italic">
                      {t("json_yaml_output_empty")}
                    </em>
                  )}
                </pre>
              ) : (
                <p className="text-sm text-red-600 break-all">
                  {t(result.errorKey)}
                  {result.errorDetail ? ` — ${result.errorDetail}` : ""}
                </p>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("json_yaml_note_block_style")}</p>
              <p>{t("json_yaml_note_types")}</p>
              <p>{t("json_yaml_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default JsonYamlConverter;
